/**
 * QBO reconciliation report shape (qbo-sync-design §4). Five buckets, each with a count + a
 * dollar total; the bucket totals sum to the header delta. The report is built from QBO
 * READ-BACKS, not our push log — sync that works silently is indistinguishable from sync that
 * fails silently. In the M3 domain layer the "read-backs" come from a mock QBO client so the
 * math is proven now; the live realm read-back arrives with the account.
 */
export const RECON_BUCKETS = ['matched', 'pending', 'failed', 'drift', 'manual'] as const;
export type ReconBucket = (typeof RECON_BUCKETS)[number];

export type ReconVerdict = 'tie' | 'delta';

export interface ReconBucketTotal {
  count: number;
  amountCents: number;
}

export type ReconBucketTotals = Record<ReconBucket, ReconBucketTotal>;

export interface ReconResult {
  verdict: ReconVerdict;
  invoicedCents: number;
  collectedCents: number;
  deltaCents: number;
  buckets: ReconBucketTotals;
}

export function emptyBuckets(): ReconBucketTotals {
  return {
    matched: { count: 0, amountCents: 0 },
    pending: { count: 0, amountCents: 0 },
    failed: { count: 0, amountCents: 0 },
    drift: { count: 0, amountCents: 0 },
    manual: { count: 0, amountCents: 0 },
  };
}

export interface ReconInvoice {
  docNumber: string;
  totalCents: number;
  /** The sync_outbox state of this invoice's QBO push. */
  sync: 'pending' | 'done' | 'failed';
}
export interface ReconReadback {
  docNumber: string;
  totalCents: number;
}

/**
 * Pure reconciliation (qbo-sync-design §4). Compares FSM issued invoices against QBO
 * read-backs to the penny and sorts each into a bucket:
 *   matched  — pushed (done) and QBO total is penny-identical (0 discrepancy)
 *   pending  — still queued (not yet in QBO) → its full amount is delta
 *   failed   — push failed/held (not in QBO) → its full amount is delta
 *   drift    — pushed but QBO total differs, OR pushed but absent in QBO → the difference
 *   manual   — refunds/credits that need manual entry (informational)
 * The pending+failed+drift amounts SUM to the header delta; verdict is `tie` only when the
 * delta is zero and nothing failed.
 */
export function reconcile(
  fsm: ReconInvoice[],
  qbo: ReconReadback[],
  collectedCents: number,
  manual: { count: number; amountCents: number } = { count: 0, amountCents: 0 },
): ReconResult {
  const buckets = emptyBuckets();
  const qboMap = new Map(qbo.map((q) => [q.docNumber, q.totalCents]));
  let invoicedCents = 0;

  for (const inv of fsm) {
    invoicedCents += inv.totalCents;
    if (inv.sync === 'pending') {
      buckets.pending.count++;
      buckets.pending.amountCents += inv.totalCents;
      continue;
    }
    if (inv.sync === 'failed') {
      buckets.failed.count++;
      buckets.failed.amountCents += inv.totalCents;
      continue;
    }
    const qboTotal = qboMap.get(inv.docNumber);
    if (qboTotal === undefined) {
      buckets.drift.count++;
      buckets.drift.amountCents += inv.totalCents; // pushed but missing in QBO
    } else if (qboTotal === inv.totalCents) {
      buckets.matched.count++;
    } else {
      buckets.drift.count++;
      buckets.drift.amountCents += inv.totalCents - qboTotal;
    }
  }

  buckets.manual = { count: manual.count, amountCents: manual.amountCents };
  const deltaCents =
    buckets.pending.amountCents + buckets.failed.amountCents + buckets.drift.amountCents;
  const verdict: ReconVerdict =
    deltaCents === 0 && buckets.failed.count === 0 ? 'tie' : 'delta';

  return { verdict, invoicedCents, collectedCents, deltaCents, buckets };
}
